import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getPeerIntelligence } from '../services/api';
import {
  Globe, Users, TrendingUp, ShieldCheck, AlertTriangle, ChevronDown, ChevronUp,
  MapPin, BarChart3, ArrowRight, CheckCircle2, Clock, Eye, Lock,
  Building2, Zap, Activity, Database, ExternalLink, Search,
  ArrowUpRight, ArrowDownRight, Rocket
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LineChart, Line, CartesianGrid } from 'recharts';

// ── Currency ────────────────────────────────────────────────

function fmt(v) {
  if (!v && v !== 0) return '€0';
  const abs = Math.abs(v);
  if (abs >= 1e6) return `€${(abs / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `€${(abs / 1e3).toFixed(1)}K`;
  return `€${abs.toFixed(0)}`;
}

// ── Strength badge ──────────────────────────────────────────

const STRENGTH = {
  strong:   { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', label: 'Strong Signal' },
  moderate: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', label: 'Moderate Signal' },
  limited:  { bg: 'bg-slate-100', text: 'text-slate-500', border: 'border-slate-200', label: 'Limited Data' },
};

// ── Adoption Curve Bar ──────────────────────────────────────

function AdoptionCurve({ early, majority, laggards }) {
  return (
    <div className="flex items-center gap-0.5 h-5 rounded-full overflow-hidden" data-testid="adoption-curve-bar">
      <div className="h-full bg-emerald-500 rounded-l-full transition-all" style={{ width: `${early}%` }} title={`Early ${early}%`} />
      <div className="h-full bg-blue-400 transition-all" style={{ width: `${majority}%` }} title={`Majority ${majority}%`} />
      <div className="h-full bg-slate-300 rounded-r-full transition-all" style={{ width: `${laggards}%` }} title={`Laggards ${laggards}%`} />
    </div>
  );
}

// ── Momentum Config ─────────────────────────────────────────

const MOMENTUM = {
  accelerating: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', label: 'Accelerating', Icon: Rocket },
  steady:       { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', label: 'Steady', Icon: ArrowRight },
  slowing:      { bg: 'bg-slate-100', text: 'text-slate-500', border: 'border-slate-200', label: 'Slowing', Icon: Clock },
};

// ── Top Mover Callout ───────────────────────────────────────

function TopMoverCallout({ mover }) {
  if (!mover) return null;
  return (
    <div className="bg-gradient-to-r from-[#0A2540] to-[#163B65] rounded-2xl p-5 mb-6 relative overflow-hidden" data-testid="top-mover-callout">
      <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-bl from-emerald-400/10 to-transparent rounded-full -translate-y-1/2 translate-x-1/2" />
      <div className="relative flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <Rocket className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <p className="text-[10px] font-bold text-emerald-400 uppercase tracking-wider mb-0.5">Fastest Mover in Your Region</p>
            <p className="text-white">
              <span className="font-bold">{mover.brand_name}</span>
              <span className="text-white/40 mx-1.5">→</span>
              <span className="font-semibold text-emerald-300">{mover.top_generic}</span>
              <span className="text-white/50 text-sm ml-2">adoption grew</span>
              <span className="text-emerald-400 font-black text-lg ml-1.5">+{mover.quarter_growth_pct}%</span>
              <span className="text-white/50 text-sm ml-1">this quarter</span>
            </p>
          </div>
        </div>
        <div className="hidden md:flex items-center gap-4 text-center">
          <div>
            <p className="text-xl font-black text-white">{mover.executing}</p>
            <p className="text-[10px] text-white/40">Executing</p>
          </div>
          <div>
            <p className="text-xl font-black text-white">{mover.total_hospitals}</p>
            <p className="text-[10px] text-white/40">Total</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Peer Trend Timeline ─────────────────────────────────────

function PeerTrendTimeline({ molecules }) {
  if (!molecules || molecules.length === 0) return null;

  // Build chart data: combine all molecules' trend data into one timeline
  const months = molecules[0]?.trend?.data?.map(d => d.month) || [];
  const chartData = months.map((month, i) => {
    const point = { month };
    molecules.forEach(mol => {
      if (mol.trend?.data?.[i]) {
        point[mol.brand_name] = mol.trend.data[i].executing;
      }
    });
    return point;
  });

  const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-6" data-testid="peer-trend-timeline">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-[#0A2540]" />
          <h2 className="text-sm font-bold text-slate-900">Adoption Trend (6 Months)</h2>
        </div>
        <div className="flex flex-wrap gap-3">
          {molecules.map((mol, i) => (
            <span key={mol.drug_id} className="flex items-center gap-1.5 text-[10px] text-slate-500">
              <span className="w-2.5 h-0.5 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
              {mol.brand_name}
            </span>
          ))}
        </div>
      </div>

      <div className="h-52">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 5, right: 10, bottom: 5, left: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} label={{ value: 'Hospitals', angle: -90, position: 'insideLeft', style: { fontSize: 10, fill: '#94a3b8' } }} />
            <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12 }} />
            {molecules.map((mol, i) => (
              <Line key={mol.drug_id} type="monotone" dataKey={mol.brand_name}
                stroke={COLORS[i % COLORS.length]} strokeWidth={2}
                dot={{ fill: COLORS[i % COLORS.length], r: 3, strokeWidth: 0 }}
                activeDot={{ r: 5 }} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Growth badges */}
      <div className="flex flex-wrap gap-2 mt-4" data-testid="growth-badges">
        {molecules.map((mol, i) => {
          const t = mol.trend || {};
          const mcfg = MOMENTUM[t.momentum] || MOMENTUM.steady;
          const MIcon = mcfg.Icon;
          return (
            <div key={mol.drug_id} className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-1.5 border border-slate-100" data-testid={`growth-badge-${mol.drug_id}`}>
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
              <span className="text-xs font-semibold text-slate-700">{mol.brand_name}</span>
              <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${t.growth_pct >= 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
                {t.growth_pct >= 0 ? <ArrowUpRight className="w-2.5 h-2.5" /> : <ArrowDownRight className="w-2.5 h-2.5" />}
                {t.growth_pct >= 0 ? '+' : ''}{t.growth_pct}%
              </span>
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${mcfg.bg} ${mcfg.text} ${mcfg.border}`}>
                <MIcon className="w-2.5 h-2.5 inline -mt-0.5 mr-0.5" />{mcfg.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Molecule Card ───────────────────────────────────────────

function MoleculeCard({ mol, expanded, onToggle }) {
  const adopt = mol.local_adoption;
  const bench = mol.savings_benchmark;
  const curve = mol.adoption_curve;
  const signals = mol.decision_signals;
  const conf = mol.confidence;
  const trend = mol.trend || {};
  const str = STRENGTH[conf.strength] || STRENGTH.limited;
  const mcfg = MOMENTUM[trend.momentum] || MOMENTUM.steady;
  const MIcon = mcfg.Icon;

  return (
    <div className={`bg-white border rounded-2xl overflow-hidden transition-all ${expanded ? 'border-slate-300 shadow-md' : 'border-slate-200 hover:shadow-sm'}`} data-testid={`peer-card-${mol.drug_id}`}>
      {/* Header Row */}
      <button onClick={onToggle} className="w-full text-left p-5 flex items-center gap-4">
        {/* Drug info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <span className="text-sm font-bold text-slate-900">{mol.brand_name}</span>
            <span className="text-slate-300">→</span>
            <span className="text-sm font-semibold text-emerald-700">{mol.top_generic}</span>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${str.bg} ${str.text} ${str.border}`}>{str.label}</span>
            {trend.quarter_growth_pct !== undefined && (
              <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${trend.quarter_growth_pct >= 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
                {trend.quarter_growth_pct >= 0 ? <ArrowUpRight className="w-2.5 h-2.5" /> : <ArrowDownRight className="w-2.5 h-2.5" />}
                {trend.quarter_growth_pct >= 0 ? '+' : ''}{trend.quarter_growth_pct}% QoQ
              </span>
            )}
            {trend.momentum && (
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${mcfg.bg} ${mcfg.text} ${mcfg.border}`}>
                <MIcon className="w-2.5 h-2.5 inline -mt-0.5 mr-0.5" />{mcfg.label}
              </span>
            )}
          </div>
          <p className="text-xs text-slate-400">{mol.therapeutic_area} &middot; {mol.top_generic_manufacturer}</p>
        </div>

        {/* Quick stats */}
        <div className="hidden md:flex items-center gap-6 flex-shrink-0">
          <div className="text-center">
            <p className="text-lg font-black text-[#0A2540]">{adopt.total_hospitals}</p>
            <p className="text-[10px] text-slate-400">Hospitals</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-black text-emerald-600">{adopt.executed}</p>
            <p className="text-[10px] text-slate-400">Executing</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-black text-slate-800">{fmt(bench.average)}</p>
            <p className="text-[10px] text-slate-400">Avg Savings</p>
          </div>
        </div>

        {/* Adoption micro-bar */}
        <div className="hidden lg:block w-32 flex-shrink-0">
          <AdoptionCurve early={curve.early_adopters_pct} majority={curve.majority_pct} laggards={curve.laggards_pct} />
          <div className="flex justify-between mt-0.5">
            <span className="text-[8px] text-emerald-500 font-bold">Early {curve.early_adopters_pct}%</span>
            <span className="text-[8px] text-slate-400">Late {curve.laggards_pct}%</span>
          </div>
        </div>

        {expanded ? <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />}
      </button>

      {/* Expanded Panel */}
      {expanded && (
        <div className="border-t border-slate-100 bg-slate-50/50 px-5 pb-5 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">

            {/* 1. Local Adoption */}
            <div>
              <div className="flex items-center gap-1.5 mb-3">
                <MapPin className="w-4 h-4 text-[#0A2540]" />
                <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Local Adoption</h3>
              </div>
              <div className="space-y-2">
                {[
                  { label: 'Reviewing', value: adopt.reviewing, color: 'bg-amber-400' },
                  { label: 'Approved', value: adopt.approved, color: 'bg-blue-500' },
                  { label: 'Executing', value: adopt.executed, color: 'bg-emerald-500' },
                ].map(item => (
                  <div key={item.label} className="flex items-center gap-2">
                    <span className="text-xs text-slate-500 w-16">{item.label}</span>
                    <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full ${item.color} rounded-full`} style={{ width: `${(item.value / adopt.total_hospitals) * 100}%` }} />
                    </div>
                    <span className="text-xs font-bold text-slate-700 w-8 text-right">{item.value}</span>
                  </div>
                ))}
                <p className="text-[10px] text-slate-400 mt-1">of {adopt.total_hospitals} hospitals tracked</p>
              </div>
            </div>

            {/* 2. Savings Benchmarks */}
            <div>
              <div className="flex items-center gap-1.5 mb-3">
                <BarChart3 className="w-4 h-4 text-[#0A2540]" />
                <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Savings Benchmark</h3>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-3 mb-2">
                <p className="text-2xl font-black text-slate-900">{fmt(bench.average)}</p>
                <p className="text-[10px] text-slate-400">Average per hospital</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-[10px] text-slate-400">Range:</span>
                  <span className="text-xs font-semibold text-slate-600">{fmt(bench.low)}</span>
                  <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden relative">
                    <div className="absolute h-full bg-gradient-to-r from-emerald-300 to-emerald-500 rounded-full" style={{ left: '15%', width: '70%' }} />
                  </div>
                  <span className="text-xs font-semibold text-slate-600">{fmt(bench.high)}</span>
                </div>
              </div>
              <div className="space-y-1">
                {bench.department_impact.map((d, i) => (
                  <div key={i} className="flex justify-between text-xs">
                    <span className="text-slate-500">{d.department}</span>
                    <span className="font-semibold text-slate-700">{fmt(d.avg_savings)} <span className="text-[10px] text-slate-400">({d.adoption_pct}%)</span></span>
                  </div>
                ))}
              </div>
            </div>

            {/* 3. Decision Signals */}
            <div>
              <div className="flex items-center gap-1.5 mb-3">
                <Zap className="w-4 h-4 text-[#0A2540]" />
                <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Decision Signals</h3>
              </div>
              <p className="text-[10px] text-emerald-600 font-bold uppercase mb-1.5">Why hospitals switched</p>
              <div className="space-y-1.5 mb-3">
                {signals.switch_reasons.map((r, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className="w-7 h-4 flex-shrink-0 bg-emerald-100 rounded-full overflow-hidden mt-0.5">
                      <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${r.pct}%` }} />
                    </div>
                    <span className="text-[11px] text-slate-600 leading-tight">{r.reason}</span>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-red-500 font-bold uppercase mb-1.5">Reasons for hesitation</p>
              <div className="space-y-1.5">
                {signals.hesitation_reasons.map((r, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className="w-7 h-4 flex-shrink-0 bg-red-100 rounded-full overflow-hidden mt-0.5">
                      <div className="h-full bg-red-400 rounded-full" style={{ width: `${r.pct}%` }} />
                    </div>
                    <span className="text-[11px] text-slate-600 leading-tight">{r.reason}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* 4. Confidence Layer */}
            <div>
              <div className="flex items-center gap-1.5 mb-3">
                <Database className="w-4 h-4 text-[#0A2540]" />
                <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Confidence Layer</h3>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-3 space-y-3">
                <div>
                  <p className="text-[10px] text-slate-400 uppercase mb-1">Data Strength</p>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${str.bg} ${str.text} ${str.border}`}>{str.label}</span>
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 uppercase mb-1">Data Points</p>
                  <p className="text-lg font-black text-slate-900">{conf.data_points.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 uppercase mb-1">Contributing Institutions</p>
                  <p className="text-lg font-black text-slate-900">{conf.contributing_institutions}</p>
                </div>
              </div>
              <Link to={`/drug/${mol.drug_id}`} className="mt-3 inline-flex items-center gap-1 text-xs text-[#0A2540] font-semibold hover:underline">
                View drug detail <ExternalLink className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── MAIN PAGE ───────────────────────────────────────────────

export default function PeerIntelligencePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    async function fetchData() {
      try {
        const res = await getPeerIntelligence();
        setData(res.data);
      } catch (err) {
        console.error('Failed to load peer intelligence:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="p-6" data-testid="peer-intelligence-loading">
        <div className="skeleton h-8 w-64 mb-6" />
        <div className="grid grid-cols-4 gap-4 mb-6">{[1,2,3,4].map(i => <div key={i} className="skeleton h-24 rounded-2xl" />)}</div>
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="skeleton h-24 rounded-2xl" />)}</div>
      </div>
    );
  }

  if (!data) {
    return <div className="p-6 text-center text-slate-500">Failed to load peer intelligence</div>;
  }

  const { region, summary, molecules, hospital_types, simulated_label, top_mover } = data;

  const filtered = searchTerm
    ? molecules.filter(m => m.brand_name.toLowerCase().includes(searchTerm.toLowerCase()) || m.generic_name.toLowerCase().includes(searchTerm.toLowerCase()) || m.therapeutic_area.toLowerCase().includes(searchTerm.toLowerCase()))
    : molecules;

  // Adoption chart data
  const chartData = molecules.slice(0, 8).map(m => ({
    name: m.brand_name,
    Executing: m.local_adoption.executed,
    Approved: m.local_adoption.approved,
    Reviewing: m.local_adoption.reviewing,
  }));

  return (
    <div className="p-6" data-testid="peer-intelligence-page">
      {/* Simulation Label */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 flex items-center gap-2 mb-6" data-testid="simulation-banner">
        <Lock className="w-4 h-4 text-amber-500 flex-shrink-0" />
        <span className="text-xs font-medium text-amber-700">{simulated_label}</span>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Peer Intelligence</h1>
          <p className="text-sm text-slate-500 mt-0.5">Anonymized insights from similar hospitals in your region</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-400 bg-slate-100 px-3 py-1.5 rounded-lg">
          <MapPin className="w-3.5 h-3.5" />
          <span>{region.name} &middot; {region.radius_km}km radius</span>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="peer-summary">
        {[
          { label: 'Institutions Tracked', value: summary.total_institutions, icon: Building2, accent: 'bg-[#0A2540]' },
          { label: 'Molecules Analyzed', value: summary.molecules_tracked, icon: Activity, accent: 'bg-blue-500' },
          { label: 'Total Executing', value: summary.total_executed, icon: CheckCircle2, accent: 'bg-emerald-500' },
          { label: 'Avg Savings/Hospital', value: fmt(summary.avg_savings_per_hospital), icon: TrendingUp, accent: 'bg-teal-500' },
        ].map((s, i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-3" data-testid={`peer-stat-${i}`}>
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${s.accent} flex-shrink-0`}>
              <s.icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-xl font-black text-slate-900">{typeof s.value === 'number' ? s.value.toLocaleString() : s.value}</p>
              <p className="text-[10px] text-slate-400 font-medium">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Top Mover Callout */}
      <TopMoverCallout mover={top_mover} />

      {/* Peer Trend Timeline */}
      <PeerTrendTimeline molecules={molecules} />

      {/* Adoption Overview Chart + Hospital Types */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-5" data-testid="adoption-chart">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#0A2540]" />
              <h2 className="text-sm font-bold text-slate-900">Adoption by Molecule</h2>
            </div>
            <div className="flex items-center gap-4 text-[10px]">
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-emerald-500" /> Executing</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-blue-400" /> Approved</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-amber-400" /> Reviewing</span>
            </div>
          </div>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical" margin={{ left: 5, right: 5 }}>
                <XAxis type="number" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#475569' }} axisLine={false} tickLine={false} width={80} />
                <Tooltip contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 12 }} />
                <Bar dataKey="Executing" stackId="a" fill="#10b981" radius={[0, 0, 0, 0]} />
                <Bar dataKey="Approved" stackId="a" fill="#60a5fa" radius={[0, 0, 0, 0]} />
                <Bar dataKey="Reviewing" stackId="a" fill="#fbbf24" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="hospital-types">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="w-5 h-5 text-[#0A2540]" />
            <h2 className="text-sm font-bold text-slate-900">Hospital Types</h2>
          </div>
          <div className="space-y-3">
            {hospital_types.map((ht, i) => {
              const max = hospital_types[0].count;
              return (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-[11px] text-slate-500 w-28 truncate text-right flex-shrink-0">{ht.type}</span>
                  <div className="flex-1 h-4 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-[#0A2540] rounded-full transition-all" style={{ width: `${(ht.count / max) * 100}%` }} />
                  </div>
                  <span className="text-xs font-bold text-slate-700 w-6 text-right">{ht.count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text" placeholder="Search molecule..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#0A2540] bg-white"
            data-testid="peer-search"
          />
        </div>
        <span className="text-xs text-slate-400">{filtered.length} of {molecules.length} molecules</span>
      </div>

      {/* Molecule Cards */}
      <div className="space-y-3" data-testid="molecule-list">
        {filtered.map(mol => (
          <MoleculeCard
            key={mol.drug_id}
            mol={mol}
            expanded={expandedId === mol.drug_id}
            onToggle={() => setExpandedId(expandedId === mol.drug_id ? null : mol.drug_id)}
          />
        ))}
        {filtered.length === 0 && (
          <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center">
            <Globe className="w-10 h-10 text-slate-200 mx-auto mb-2" />
            <p className="text-sm text-slate-500">No molecules match your search</p>
          </div>
        )}
      </div>

      {/* Privacy Notice */}
      <div className="mt-8 bg-slate-50 border border-slate-200 rounded-xl px-5 py-4 flex items-start gap-3" data-testid="privacy-notice">
        <ShieldCheck className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-xs font-semibold text-slate-600 mb-1">Privacy & Data Integrity</p>
          <p className="text-[11px] text-slate-400 leading-relaxed">
            All peer data is fully anonymized. No identifiable hospital information, direct pricing, or patient data is disclosed.
            Only aggregated ranges from contributing institutions are displayed. Data strength indicators reflect the volume
            and consistency of contributing sources. This module uses simulated market data for demonstration purposes.
          </p>
        </div>
      </div>
    </div>
  );
}
